// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import {
	getArrayDiff,
	getObjectKeys,
	NullPartial,
	omitKeys,
	pickKeys,
	subsetTypeHasKey,
	ValueOf,
} from "@hex-insights/core";
import {
	SharingPolicyCreateInput,
	SharingPolicyDetailQuery,
	SharingPolicyInAnnouncementCreateInput,
	SharingPolicyInCalendarEventCreateInput,
	SharingPolicyInNewsletterCreateInput,
	SharingPolicyInPostCreateInput,
	SharingPolicyUpdateInput,
} from "../../../Utilities";
import * as SharingPolicyFormValues from "./form-values";

/**
 * Converts the given `formValues` for the create form to a GraphQL create input for the SharingPolicy model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInput<K extends keyof SharingPolicyFormValues.Create = keyof SharingPolicyFormValues.Create>(
	formValues: SharingPolicyFormValues.CreateSubset<K>,
): SharingPolicyCreateInput {
	const input: NullPartial<SharingPolicyCreateInput> = { ...formValues };
	return input as SharingPolicyCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the Announcement to a GraphQL nested create input for the SharingPolicy model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInAnnouncementInput<
	K extends keyof SharingPolicyFormValues.CreateInAnnouncement = keyof SharingPolicyFormValues.CreateInAnnouncement,
>(formValues: SharingPolicyFormValues.CreateInAnnouncementSubset<K>): SharingPolicyInAnnouncementCreateInput {
	const input: NullPartial<SharingPolicyInAnnouncementCreateInput> = { ...formValues };
	return input as SharingPolicyInAnnouncementCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the CalendarEvent to a GraphQL nested create input for the SharingPolicy model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInCalendarEventInput<
	K extends keyof SharingPolicyFormValues.CreateInCalendarEvent = keyof SharingPolicyFormValues.CreateInCalendarEvent,
>(formValues: SharingPolicyFormValues.CreateInCalendarEventSubset<K>): SharingPolicyInCalendarEventCreateInput {
	const input: NullPartial<SharingPolicyInCalendarEventCreateInput> = { ...formValues };
	return input as SharingPolicyInCalendarEventCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the Newsletter to a GraphQL nested create input for the SharingPolicy model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInNewsletterInput<
	K extends keyof SharingPolicyFormValues.CreateInNewsletter = keyof SharingPolicyFormValues.CreateInNewsletter,
>(formValues: SharingPolicyFormValues.CreateInNewsletterSubset<K>): SharingPolicyInNewsletterCreateInput {
	const input: NullPartial<SharingPolicyInNewsletterCreateInput> = { ...formValues };
	return input as SharingPolicyInNewsletterCreateInput;
}

/**
 * Converts the given `formValues` for the create form nested in the Post to a GraphQL nested create input for the SharingPolicy model.
 *
 * @param formValues The form values to convert.
 */
export function toGQLCreateInPostInput<
	K extends keyof SharingPolicyFormValues.CreateInPost = keyof SharingPolicyFormValues.CreateInPost,
>(formValues: SharingPolicyFormValues.CreateInPostSubset<K>): SharingPolicyInPostCreateInput {
	const input: NullPartial<SharingPolicyInPostCreateInput> = { ...formValues };
	return input as SharingPolicyInPostCreateInput;
}

/**
 * Converts the given `formValues` for the detail form to a GraphQL update input for the SharingPolicy model.
 *
 * @param formValues The form values to convert. Generally only the changed form values.
 * @param initialFormValues The initial values of the form.
 */
export function toGQLUpdateInput<K extends keyof SharingPolicyFormValues.Detail = keyof SharingPolicyFormValues.Detail>(
	formValues: Partial<SharingPolicyFormValues.DetailSubset<K>>,
	initialFormValues: SharingPolicyFormValues.DetailSubset<K>,
): SharingPolicyUpdateInput {
	const inputFormValues = omitKeys(formValues, [
		"announcementID",
		"calendarEventID",
		"newsletterID",
		"postID",
		"userGroupIDs",
		"userIDs",
	]);
	const input: SharingPolicyUpdateInput = { ...inputFormValues };

	if (
		subsetTypeHasKey(formValues, "userGroupIDs") &&
		subsetTypeHasKey(initialFormValues, "userGroupIDs") &&
		formValues.userGroupIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.userGroupIDs, formValues.userGroupIDs);
		input.addUserGroupIDs = added;
		input.removeUserGroupIDs = removed;
	}

	if (
		subsetTypeHasKey(formValues, "userIDs") &&
		subsetTypeHasKey(initialFormValues, "userIDs") &&
		formValues.userIDs !== undefined
	) {
		const { added, removed } = getArrayDiff(initialFormValues.userIDs, formValues.userIDs);
		input.addUserIDs = added;
		input.removeUserIDs = removed;
	}

	return input;
}

export type SharingPolicyFormKeyMap = {
	isPublic: "isPublic";
	announcementID: "announcement";
	calendarEventID: "calendarEvent";
	newsletterID: "newsletter";
	postID: "post";
	userGroupIDs: "userGroups";
	userIDs: "users";
};

export type ToFormValuesArgs<K extends keyof SharingPolicyFormValues.Detail = keyof SharingPolicyFormValues.Detail> = {
	sharingPolicy: Pick<SharingPolicyDetailQuery["sharingPolicy"], ValueOf<Pick<SharingPolicyFormKeyMap, K>>>;
	fields?: { [Key in K]: true };
};

/**
 * Converts a SharingPolicy instance to its corresponding detail form values.
 *
 * @param sharingPolicy The instance to convert.
 * @param fields The subset of fields to include.
 */
export function toFormValues<K extends keyof SharingPolicyFormValues.Detail = keyof SharingPolicyFormValues.Detail>({
	sharingPolicy,
	fields,
}: ToFormValuesArgs<K>): SharingPolicyFormValues.DetailSubset<K> {
	const formValues = {
		isPublic: subsetTypeHasKey(sharingPolicy, "isPublic") ? sharingPolicy.isPublic : false,
		announcementID: subsetTypeHasKey(sharingPolicy, "announcement") ? sharingPolicy.announcement?.id ?? null : null,
		calendarEventID: subsetTypeHasKey(sharingPolicy, "calendarEvent") ? sharingPolicy.calendarEvent?.id ?? null : null,
		newsletterID: subsetTypeHasKey(sharingPolicy, "newsletter") ? sharingPolicy.newsletter?.id ?? null : null,
		postID: subsetTypeHasKey(sharingPolicy, "post") ? sharingPolicy.post?.id ?? null : null,
		userGroupIDs: subsetTypeHasKey(sharingPolicy, "userGroups") ? sharingPolicy.userGroups.map((e) => e.id) : [],
		userIDs: subsetTypeHasKey(sharingPolicy, "users") ? sharingPolicy.users.map((e) => e.id) : [],
	};
	return fields ? pickKeys(formValues, getObjectKeys(fields)) : formValues;
}
